/**
 * Bridge Card Loader Component
 * Handles async loading of Bridge Card data from Objective artifact_item
 * @feature BRIDGE_CARD - Load Bridge Card from objective artifact_item id
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, Alert, Button, message, Typography } from 'antd';
import { useBridgeSession } from '../../contexts/BridgeSessionContext';
import { PrinterOutlined, DownloadOutlined, FileTextOutlined, EditOutlined } from '@ant-design/icons';
import { BridgeCard } from '../BridgeCard/BridgeCard';
import { BridgeCardObjective } from '../../types/bridgeCard';
import { bridgeSessionService } from '../../services/bridgeSessionService';
import { downloadQuestionsAnswersExport } from '../../services/localBridgeSessionApi';
import { isLocalBridgeApiEnabled } from '../../services/bridgeSessionDataAdapter';
import { EditBridgeSessionObjectiveModal } from '../LocalBridge/EditBridgeSessionObjectiveModal';
import styles from './BridgeCardLoader.module.css';

interface BridgeCardLoaderProps {
  objectiveArtifactItemId: string;
}

export const BridgeCardLoader: React.FC<BridgeCardLoaderProps> = ({ objectiveArtifactItemId }) => {
  const navigate = useNavigate();
  const { sessionType } = useBridgeSession();
  const [objective, setObjective] = useState<BridgeCardObjective | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editObjectiveOpen, setEditObjectiveOpen] = useState(false);

  useEffect(() => {
    const loadBridgeCard = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load Bridge Card data from Objective artifact_item
        const bridgeCardData = await bridgeSessionService.getBridgeCardFromObjective(objectiveArtifactItemId, sessionType);
        setObjective(bridgeCardData);
      } catch (err) {
        console.error('Failed to load Bridge Card data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load Bridge Card');
        setObjective(null);
        // No mock fallback: bridge card data must come from the bridge session
      } finally {
        setLoading(false);
      }
    };

    loadBridgeCard();
  }, [objectiveArtifactItemId, sessionType]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large">
          <div className={styles.loadingContent}>Loading Bridge Card...</div>
        </Spin>
      </div>
    );
  }

  if (error && !objective) {
    return (
      <div className={styles.errorContainer}>
        <Alert
          type="error"
          message="Error Loading Bridge Card"
          description={error}
          showIcon
        />
      </div>
    );
  }

  if (!objective) {
    return (
      <div className={styles.emptyState}>
        <p>No Bridge Card data available.</p>
      </div>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadQuestionsAnswers = async () => {
    const ok = await downloadQuestionsAnswersExport(objectiveArtifactItemId);
    if (ok) message.success('Download started.');
    else message.error('Download failed.');
  };

  const handleEditObjectiveSaved = async () => {
    try {
      const bridgeCardData = await bridgeSessionService.getBridgeCardFromObjective(objectiveArtifactItemId);
      setObjective(bridgeCardData);
    } catch {
      // Keep current objective on refetch failure
    }
  };

  return (
    <div className={styles.bridgeCardContainer}>
      <Typography.Title level={4} style={{ marginBottom: 16 }}>
        Bridge Card - Business Domain
      </Typography.Title>
      <div className={styles.actionsBar}>
        <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
          Print / Export to PDF
        </Button>
        {isLocalBridgeApiEnabled() && (
          <>
            <Button icon={<EditOutlined />} onClick={() => setEditObjectiveOpen(true)}>
              Edit objective
            </Button>
            <Button icon={<FileTextOutlined />} onClick={() => navigate(`/bridge-session/${objectiveArtifactItemId}/report`)}>
              View full report
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadQuestionsAnswers}>
              Download questions & answers (JSON)
            </Button>
          </>
        )}
      </div>
      <EditBridgeSessionObjectiveModal
        sessionId={objectiveArtifactItemId}
        open={editObjectiveOpen}
        onClose={() => setEditObjectiveOpen(false)}
        onSaved={handleEditObjectiveSaved}
      />
      {error && (
        <Alert
          type="warning"
          message="Using fallback data"
          description={error}
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}
      <BridgeCard
        objective={objective}
        onUpdate={(updated) => {
          // Handle objective update
          console.log('Objective updated:', updated);
          setObjective(updated);
        }}
      />
    </div>
  );
};
